// ==========================================================================
// Firebase - App initialization and Cloud Messaging
// ==========================================================================

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, type Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

let messagingInstance: Messaging | null = null;

/**
 * Get the Firebase Messaging instance (lazy, only if browser supports it).
 * Returns null on unsupported browsers (e.g. Firefox private browsing, some iOS).
 */
export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;

  const supported = await isSupported();
  if (!supported) return null;

  messagingInstance = getMessaging(app);
  return messagingInstance;
}

/**
 * Request notification permission and get the FCM registration token.
 * Returns null if permission denied or messaging unsupported.
 *
 * Firebase SDK auto-registers `/firebase-messaging-sw.js` on first call,
 * so we don't manually register it (which would conflict with vite-plugin-pwa's SW).
 */
export async function requestFcmToken(): Promise<string | null> {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;

  // Only prompt if the user hasn't answered yet — avoids re-prompting on every launch
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return null;

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

  const token = await getToken(messaging, { vapidKey });

  return token;
}

/**
 * Listen for foreground messages. Call this once after login.
 * Returns an unsubscribe function.
 */
export function onForegroundMessage(callback: (payload: unknown) => void): () => void {
  let active = true;
  let unsubscribe: (() => void) | null = null;

  getFirebaseMessaging().then((messaging) => {
    if (!messaging || !active) return;
    unsubscribe = onMessage(messaging, (payload) => {
      callback(payload);
    });
  });

  return () => {
    active = false;
    if (unsubscribe) unsubscribe();
  };
}
